import { createContext, useContext, useState, useEffect } from 'react'
import {
  fetchTenantConfig,
  isWorkitPlatformHostname,
  resolveSubdomainForTenantFetch,
} from '../api/tenantConfig'
import { applyTenantTheme } from '../theme/tenantTheme'
import { resolveWebsiteComponentKey } from '../api/websiteComponentMap'
import { fetchTenantVehicles, fetchTenantTestimonials } from '../api/publicApi'
import { VEHICLES as MOCK_VEHICLES } from '../data/vehicles'

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true'
import './TenantConfigContext.css'

const TenantConfigContext = createContext(null)

function missingTenantMessage() {
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const host = window.location.hostname
    if (!isWorkitPlatformHostname(host)) {
      return `No se pudo resolver el dominio ${host}. Verificá que esté activo en Workit.`
    }
  }
  return 'No se detectó subdominio. En local usá http://tu-tenant.localhost:5177 o http://localhost:5177/?subdomain=tu-tenant'
}

function TenantConfigLoading() {
  return (
    <div className="tenant-config-loading" role="status" aria-live="polite">
      <div className="tenant-config-loading__spinner" />
    </div>
  )
}

function TenantConfigError({ message, onRetry }) {
  return (
    <div className="tenant-config-error">
      <p>{message}</p>
      <button type="button" onClick={onRetry}>
        Reintentar
      </button>
    </div>
  )
}

export function TenantConfigProvider({ children, tenantId, subdomain }) {
  const [config, setConfig] = useState(null)
  const [vehicles, setVehicles] = useState([])
  const [testimonials, setTestimonials] = useState([])
  const [resolvedSubdomain, setResolvedSubdomain] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [retryKey, setRetryKey] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(null)

    async function load() {
      try {
        const propSub = subdomain && String(subdomain).trim() ? String(subdomain).trim() : null
        const sub = propSub || (await resolveSubdomainForTenantFetch())
        if (cancelled) return

        setResolvedSubdomain(sub || null)

        const tenantIdForFetch = sub ? undefined : tenantId

        if (!sub && !tenantIdForFetch && !USE_MOCK) {
          throw new Error(missingTenantMessage())
        }

        const vehiclesPromise = USE_MOCK
          ? Promise.resolve(MOCK_VEHICLES)
          : sub
            ? fetchTenantVehicles(sub)
            : Promise.resolve([])

        const testimonialsPromise = USE_MOCK
          ? Promise.resolve([])
          : sub
            ? fetchTenantTestimonials(sub)
            : Promise.resolve([])

        const [data, vehicleList, testimonialList] = await Promise.all([
          fetchTenantConfig(tenantIdForFetch, sub || undefined),
          vehiclesPromise,
          testimonialsPromise,
        ])

        if (cancelled) return
        setConfig(data)
        setVehicles(Array.isArray(vehicleList) ? vehicleList : [])
        setTestimonials(Array.isArray(testimonialList) ? testimonialList : [])
        setError(null)
      } catch (err) {
        if (cancelled) return
        setError(err)
        setConfig(null)
        setVehicles([])
        setTestimonials([])
        setResolvedSubdomain(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void load()

    return () => {
      cancelled = true
    }
  }, [tenantId, subdomain, retryKey])

  useEffect(() => {
    applyTenantTheme(config?.theme)
  }, [config?.theme])

  useEffect(() => {
    if (!config?.brand?.name) return
    document.title = config.brand.name
    const fav = config.faviconImage
    if (typeof fav === 'string' && fav.length > 0) {
      let link = document.querySelector("link[rel='icon']")
      if (!link) {
        link = document.createElement('link')
        link.rel = 'icon'
        document.head.appendChild(link)
      }
      link.href = fav
    }
  }, [config])

  const value = {
    config,
    vehicles,
    testimonials,
    loading,
    error,
    subdomain: resolvedSubdomain,
    publicInfo: config?.publicInfo ?? null,
    website: config?.website ?? null,
    websiteLayout: config?.websiteLayout ?? null,
    getComponentKey: (type) => resolveWebsiteComponentKey(type, config),
  }

  if (loading) {
    return <TenantConfigLoading />
  }

  if (error) {
    return (
      <TenantConfigError
        message={error?.message || 'No se pudo cargar la configuración del sitio.'}
        onRetry={() => setRetryKey((k) => k + 1)}
      />
    )
  }

  return (
    <TenantConfigContext.Provider value={value}>
      {children}
    </TenantConfigContext.Provider>
  )
}

export function useTenantConfig() {
  const ctx = useContext(TenantConfigContext)
  if (!ctx) {
    throw new Error('useTenantConfig debe usarse dentro de TenantConfigProvider')
  }
  return ctx
}
